import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Search, Stethoscope, MapPin, Building2, ClipboardList, ChevronLeft, ChevronRight } from "lucide-react";
import KPICard from "../components/ui/KPICard";
import SEO from "../components/SEO";

function fmt(n) {
  return n == null ? "—" : Number(n).toLocaleString("pt-BR");
}

function capitalize(str) {
  if (!str) return "";
  return str.toLowerCase().replace(/(^|\s)\S/g, (l) => l.toUpperCase());
}

function formatCnpj(cnpj) {
  if (!cnpj || cnpj.length !== 14) return cnpj || null;
  return cnpj.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5");
}

function StatsCard({ title, icon, children }) {
  return (
    <div className="bg-[var(--card)] border border-[var(--border)] rounded-xl p-5">
      <div className="flex items-center gap-2 mb-4">
        {icon && <span className="text-[var(--accent)]">{icon}</span>}
        <h2 className="text-lg font-bold text-[var(--text)]">{title}</h2>
      </div>
      {children}
    </div>
  );
}

function HBar({ items, labelKey, valueKey, color, valueFmt }) {
  const maxVal = Math.max(...items.map((r) => r[valueKey] || 0), 1);
  const format = valueFmt || ((v) => fmt(v));
  return (
    <div className="space-y-2">
      {items.map((r) => (
        <div key={r[labelKey]} className="flex items-center gap-2">
          <span className="text-xs text-[var(--text)] w-28 truncate" title={r[labelKey]}>
            {capitalize(r[labelKey])}
          </span>
          <div className="flex-1 h-3 bg-[var(--border)] rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-500"
              style={{ width: `${(r[valueKey] / maxVal) * 100}%`, backgroundColor: color, opacity: 0.65 }}
            />
          </div>
          <span className="text-xs text-[var(--muted)] w-16 text-right">{format(r[valueKey], r)}</span>
        </div>
      ))}
    </div>
  );
}

const PER_PAGE = 24;

export default function MedPage() {
  const [summary, setSummary] = useState(null);
  const [filtros, setFiltros] = useState({ ufs: [], servicos: [] });

  const [query, setQuery] = useState("");
  const [uf, setUf] = useState("");
  const [servico, setServico] = useState("");

  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const debounceRef = useRef(null);
  const requestIdRef = useRef(0);

  useEffect(() => {
    axios.get("/med/summary").then((res) => {
      if (res.data?.success) setSummary(res.data.data);
    }).catch(() => {});
    axios.get("/med/filtros").then((res) => {
      if (res.data?.success) setFiltros(res.data.data);
    }).catch(() => {});
  }, []);

  const fetchEstabelecimentos = useCallback((params) => {
    const id = ++requestIdRef.current;
    setLoading(true);
    axios.get("/med/estabelecimentos", { params })
      .then((res) => {
        if (requestIdRef.current !== id) return;
        if (res.data?.success) {
          setResults(res.data.data);
          setTotal(res.data.total);
          setTotalPages(res.data.totalPages);
        }
        setError(null);
      })
      .catch((err) => {
        if (requestIdRef.current !== id) return;
        setError(err.response?.data?.error || err.message);
      })
      .finally(() => {
        if (requestIdRef.current === id) setLoading(false);
      });
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchEstabelecimentos({ q: query, uf, servico, page, limit: PER_PAGE });
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [query, uf, servico, page, fetchEstabelecimentos]);

  const handleFilterChange = (setter) => (e) => {
    setPage(1);
    setter(e.target.value);
  };

  const seoDescription = summary?.total != null
    ? `Encontre ${fmt(summary.total)} estabelecimentos de saúde no Brasil (hospitais, clínicas, laboratórios), organizados por ${fmt(summary.totalServicos)} serviços especializados, com dados oficiais do CNES/DATASUS.`
    : "Encontre estabelecimentos de saúde no Brasil por serviço especializado, com dados oficiais do CNES/DATASUS.";

  return (
    <div className="space-y-6">
      <SEO
        title="Hospitais e Clínicas por Especialidade (CNES)"
        description={seoDescription}
        path="/med"
      />
      <div className="border-b border-[var(--border)] pb-6">
        <div className="flex items-center gap-2">
          <Stethoscope size={24} className="text-[var(--accent)]" />
          <h1 className="text-3xl font-extrabold tracking-tight text-[var(--text)]">Med do Povo</h1>
        </div>
        <p className="text-sm text-[var(--muted)] mt-1">
          Estabelecimentos de saúde no Brasil, por serviço especializado (CNES/DATASUS).
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
        {/* ── Left: Buscar Estabelecimento ── */}
        <div className="lg:col-span-2 bg-[var(--card)] border border-[var(--border)] rounded-xl p-5">
          <h2 className="text-lg font-bold text-[var(--text)] mb-3">Buscar Estabelecimento</h2>
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[var(--muted)]" />
              <input
                type="text"
                value={query}
                onChange={handleFilterChange(setQuery)}
                placeholder="Buscar por nome, CNES, CNPJ ou município..."
                className="w-full pl-10 pr-4 py-2.5 bg-[var(--background)] border border-[var(--border)] rounded-lg text-sm text-[var(--text)] placeholder:text-[var(--muted)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]"
              />
            </div>
            <select
              value={uf}
              onChange={handleFilterChange(setUf)}
              className="px-3 py-2.5 bg-[var(--background)] border border-[var(--border)] rounded-lg text-sm text-[var(--text)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]"
            >
              <option value="">Todos os estados</option>
              {filtros.ufs.map((u) => <option key={u} value={u}>{u}</option>)}
            </select>
            <select
              value={servico}
              onChange={handleFilterChange(setServico)}
              className="px-3 py-2.5 bg-[var(--background)] border border-[var(--border)] rounded-lg text-sm text-[var(--text)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)] max-w-[240px]"
            >
              <option value="">Todos os serviços</option>
              {filtros.servicos.map((s) => (
                <option key={s.codigo} value={s.codigo}>{capitalize(s.nome)}</option>
              ))}
            </select>
          </div>

          {!loading && (
            <p className="text-xs text-[var(--muted)] mt-3">
              {fmt(total)} resultado{total !== 1 ? "s" : ""}
            </p>
          )}

          {error && (
            <div className="mt-4 bg-red-500/10 border border-red-500/20 text-red-500 rounded-lg p-4 text-sm">
              <strong>Erro ao carregar estabelecimentos:</strong> {error}
            </div>
          )}

          {/* Results */}
          <div className="mt-4 space-y-1">
            {results.map((r) => (
              <Link
                to={`/med/${r.cnes}`}
                key={`${r.cnes}-${r.servicoCodigo}`}
                className="flex items-center gap-3 py-2.5 px-3 rounded-lg hover:bg-[var(--border)]/20 transition-colors border-b border-[var(--border)] last:border-b-0"
              >
                <div className="flex-shrink-0 w-9 h-9 rounded-xl bg-sky-500/10 border border-sky-500/20 flex items-center justify-center">
                  <Stethoscope size={16} className="text-sky-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-[var(--text)] truncate">{capitalize(r.nomeEmpresarial || r.estabelecimento)}</span>
                  </div>
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-[var(--muted)]">
                    <span className="font-mono">CNES {r.cnes}</span>
                    {formatCnpj(r.cnpj) && (
                      <>
                        <span>&middot;</span>
                        <span className="font-mono">{formatCnpj(r.cnpj)}</span>
                      </>
                    )}
                    <span>&middot;</span>
                    <span>{capitalize(r.municipio)}, {r.uf}</span>
                    {r.servicoNome && (
                      <>
                        <span>&middot;</span>
                        <span className="font-semibold text-[var(--accent)]">{capitalize(r.servicoNome)}</span>
                      </>
                    )}
                  </div>
                </div>
              </Link>
            ))}

            {!loading && results.length === 0 && (
              <div className="text-center py-16 text-[var(--muted)]">
                <Stethoscope size={32} className="mx-auto mb-3 opacity-40" />
                <p className="text-sm font-medium">Nenhum estabelecimento encontrado</p>
              </div>
            )}

            {loading && (
              <div className="text-center py-16 text-[var(--muted)]">
                <div className="w-8 h-8 border-4 border-sky-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
                <p className="text-sm font-medium">Carregando estabelecimentos...</p>
              </div>
            )}
          </div>

          {/* Pagination */}
          {!loading && totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 pt-4 mt-2 border-t border-[var(--border)]">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-2 rounded-lg text-xs font-semibold bg-[var(--background)] border border-[var(--border)] text-[var(--muted)] disabled:opacity-40 hover:border-sky-500/50 transition-all"
              >
                <ChevronLeft size={14} />
              </button>
              <span className="text-xs text-[var(--muted)] px-2">
                {page} / {totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="p-2 rounded-lg text-xs font-semibold bg-[var(--background)] border border-[var(--border)] text-[var(--muted)] disabled:opacity-40 hover:border-sky-500/50 transition-all"
              >
                <ChevronRight size={14} />
              </button>
            </div>
          )}
        </div>

        {/* ── Right: Estatísticas ── */}
        <div className="lg:col-span-1 space-y-6">
          {summary && (
            <div className="grid grid-cols-2 gap-3">
              <KPICard label="Estabelecimentos" value={fmt(summary.total)} icon={<Stethoscope size={20} />} />
              <KPICard label="Estados" value={fmt(summary.totalUf)} icon={<MapPin size={20} />} />
              <KPICard label="Municípios" value={fmt(summary.totalMunicipios)} icon={<Building2 size={20} />} />
              <KPICard label="Serviços" value={fmt(summary.totalServicos)} icon={<ClipboardList size={20} />} />
            </div>
          )}

          {summary && (
            <StatsCard title="Estabelecimentos por Estado" icon={<MapPin size={16} />}>
              <HBar items={summary.porUf.slice(0, 12)} labelKey="uf" valueKey="total" color="#0ea5e9" />
            </StatsCard>
          )}

          {summary && (
            <StatsCard title="Principais Serviços" icon={<ClipboardList size={16} />}>
              <HBar
                items={summary.porServico.slice(0, 12).map((r) => ({ ...r, servicoNomeCurto: capitalize(r.servicoNome) }))}
                labelKey="servicoNomeCurto"
                valueKey="total"
                color="#0ea5e9"
              />
            </StatsCard>
          )}
        </div>
      </div>
    </div>
  );
}
